import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { ScanFace, ArrowRight } from "lucide-react";
import api, { API } from "../lib/api";
import ResultCard from "../components/ResultCard";
import EmailCapture from "../components/EmailCapture";
import { useAuth, startLogin } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { SITE_URL } from "../lib/brand";

export default function ResultPage() {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [result, setResult] = useState(location.state?.result || null);
  const [loading, setLoading] = useState(!location.state?.result);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (location.state?.result && location.state.result.id === id) return;
    setLoading(true);
    api.get(`/scans/${id}`)
      .then(({ data }) => setResult(data))
      .catch((e) => setError(e?.response?.data?.detail || "Result not found"))
      .finally(() => setLoading(false));
  }, [id, location.state]);

  const score = result?.overall_score;
  useSeo({
    title: result ? `${result.display_name || "Face"} scored ${score}/100` : "FaceCard result",
    description: result
      ? `${result.display_name || "Someone"} scored ${score}/100 on Aesthetiq with a ${result.face_shape} face shape. Analyse your own face free.`
      : "View this AI facial analysis FaceCard on Aesthetiq.",
    path: `/r/${id}`,
    image: `${API}/og/${id}.png`,
    type: "article",
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3">
        <ScanFace className="h-8 w-8 animate-pulse text-primary" />
        <p className="text-sm text-muted-foreground">Loading result…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <h1 className="font-display text-3xl">{error}</h1>
        <Link to="/" className="rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">Scan your face</Link>
      </div>
    );
  }

  const shareUrl = `${SITE_URL}/r/${id}`;
  const saved = result?.saved;

  return (
    <div className="container py-12">
      <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <span className="label-eyebrow">FaceCard result</span>
          <h1 className="mt-2 font-display text-4xl">The verdict is in.</h1>
        </div>
        <Link to="/" className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-semibold transition hover:border-primary/50" data-testid="scan-again-btn">
          Scan again <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {!user && saved === false && (
        <div className="mb-8 flex flex-col items-start justify-between gap-4 rounded-xl border border-primary/30 bg-primary/5 p-5 sm:flex-row sm:items-center" data-testid="save-prompt">
          <div>
            <p className="font-display text-lg">Save this result & track your progress</p>
            <p className="text-sm text-muted-foreground">Create a free account to keep your history, join the leaderboard and unlock the full coach plan.</p>
          </div>
          <button onClick={startLogin} className="shrink-0 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Sign up free</button>
        </div>
      )}

      <ResultCard result={result} shareUrl={shareUrl} />

      <div className="mt-8">
        <EmailCapture scanId={id} name={result?.display_name} />
      </div>
    </div>
  );
}
